import { view } from '@yola/ws-sdk';
import hasElementConflict from '../verifiers/has-element-conflict';

const getElementIdFromAction = (action) => {
  if (!action || !action.payload) {
    return null;
  }

  if (action.type === view.actionTypes.REPLACE_ELEMENT) {
    return action.payload.targetElementId || null;
  }

  return action.payload.elementId || null;
};

const findReplaceActionForElement = (replaceActions, elementId) =>
  replaceActions.find((replaceAction) =>
    hasElementConflict(elementId, getElementIdFromAction(replaceAction))
  );

const removeDuplicateReplaceActions = (replaceActions) => {
  const seenElementIds = [];

  return replaceActions.filter((action) => {
    const elementId = getElementIdFromAction(action);
    const isDuplicate = seenElementIds.some((seenId) => hasElementConflict(elementId, seenId));

    if (isDuplicate) {
      // eslint-disable-next-line no-console
      console.warn(
        `Conflict detected: Multiple replace actions for element ${elementId}. Keeping first, removing duplicate.`
      );

      return false;
    }

    seenElementIds.push(elementId);

    return true;
  });
};

const partitionAttributeActions = (attributeActions, replaceActions) => {
  const conflicting = [];
  const nonConflicting = [];

  attributeActions.forEach((attributeAction) => {
    const elementId = getElementIdFromAction(attributeAction);
    const conflictingReplaceAction = findReplaceActionForElement(replaceActions, elementId);

    if (conflictingReplaceAction) {
      conflicting.push(attributeAction);
    } else {
      nonConflicting.push(attributeAction);
    }
  });

  return { conflicting, nonConflicting };
};

const applyAttributeActionToElement = (element, action) => {
  const { attr, value } = action.payload || {};

  if (action.type === view.actionTypes.REMOVE_ELEMENT_ATTRIBUTE) {
    element.removeAttribute(attr);
    return;
  }

  element.setAttribute(attr, value);
};

const applyAttributesToReplaceElements = (conflictingAttributeActions, replaceActions) => {
  conflictingAttributeActions.forEach((attributeAction) => {
    const elementId = getElementIdFromAction(attributeAction);
    const replaceAction = findReplaceActionForElement(replaceActions, elementId);
    const { element } = replaceAction?.payload || {};

    if (element) {
      applyAttributeActionToElement(element, attributeAction);

      // eslint-disable-next-line no-console
      console.warn(
        `Conflict detected: Attribute action for element ${elementId} conflicts with replace action. Transferring attribute to replace element.`
      );
    }
  });
};

const resolveAndMergeActions = (customActions = [], attributeActions = []) => {
  const customReplaceActions = customActions.filter(
    (action) => action.type === view.actionTypes.REPLACE_ELEMENT
  );
  const customAttributeActions = customActions.filter(
    (action) => action.type !== view.actionTypes.REPLACE_ELEMENT
  );
  const allAttributeActions = [...attributeActions, ...customAttributeActions];

  const replaceActions = removeDuplicateReplaceActions(customReplaceActions);
  const { conflicting, nonConflicting } = partitionAttributeActions(
    allAttributeActions,
    replaceActions
  );

  if (conflicting.length > 0) {
    applyAttributesToReplaceElements(conflicting, replaceActions);
  }

  return [...replaceActions, ...nonConflicting];
};

export default resolveAndMergeActions;
